import { useEffect, useId, useRef, useState } from 'react'
import { useDismiss } from '../lib/useDismiss'
import { PickerFoot } from './PickerFoot'
import { useHubLang } from '../lib/i18n'
import { COMPOSE } from '../lib/hubCopy'
import {
  activeMentionQuery,
  insertMention,
  reconcile,
  searchMentionable,
  type MentionablePerson,
  type MentionDraft,
} from '../lib/mentions'

/**
 * A textarea that can address a colleague by name.
 *
 * ── IT IS A TEXTAREA FIRST AND AN AUTOCOMPLETE SECOND ───────────────────────
 *
 * Everything works with the menu never opening. Type, paste, submit — all
 * unchanged. The @ menu is an offer laid over an ordinary box, which matters
 * because this replaces the compose control on a screen ~900 people use and a
 * clever input that eats a keystroke on a cheap tablet is a worse box than the
 * plain one it replaced.
 *
 * So: no contentEditable, no rich text, no shadow DOM overlay. The value is a
 * string, the token goes in as text, and the parent's state handling does not
 * change at all.
 *
 * ── THE PICKER IS THE ONLY WAY A MENTION IS MADE ────────────────────────────
 *
 * Choosing from this list is what marks a person. Typing "@Jamie" and pressing
 * space writes the literal text "@Jamie" and mentions nobody, which is correct
 * and is the whole design: the server never matches a name (see lib/mentions.ts
 * and the 059 header), so a mention that was never picked was never made.
 *
 * That has a visible consequence worth keeping: there is no way to "half"
 * mention somebody. Either the chip appears in the posted reply or it does not.
 *
 * ── THE WRITER SEES A NAME, NOT A GUID (13 Sep) ─────────────────────────────
 *
 * This header used to claim the composer "renders over" the token. It did not,
 * and the writer stared at `@{6f1c…}` mid-sentence until they pressed send.
 *
 * The value is a MentionDraft now: the text a person would type, plus spans
 * saying which stretches point at whom. `toStorage` swaps the spans back to
 * tokens at submit, so nothing the database sees has changed. Editing inside a
 * mention drops its span and leaves ordinary text — see reconcile.
 *
 * ── KEYBOARD ────────────────────────────────────────────────────────────────
 *
 * The combobox pattern Picker already uses: focus never leaves the textarea,
 * ArrowUp/Down move a highlight tracked by aria-activedescendant, Enter and Tab
 * take the highlighted row, Escape closes the menu WITHOUT closing the thread
 * or clearing the draft. Enter only commits while the menu is open — with it
 * shut, Enter is a newline like any other textarea, because a compose box where
 * Enter sometimes submits and sometimes does not is how a half-written reply
 * gets posted.
 */
export function MentionBox({
  value,
  onChange,
  placeholder,
  rows = 2,
  maxLength = 3000,
  id,
  ariaLabel,
  autoFocus = false,
  disabled = false,
}: {
  value: MentionDraft
  onChange: (next: MentionDraft) => void
  placeholder?: string
  rows?: number
  maxLength?: number
  id?: string
  ariaLabel?: string
  autoFocus?: boolean
  disabled?: boolean
}) {
  const lang = useHubLang()
  const boxRef = useRef<HTMLTextAreaElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [from, setFrom] = useState(0)
  const [people, setPeople] = useState<MentionablePerson[]>([])
  const [active, setActive] = useState(0)
  /** What matched, from the server (088). REC-54. */
  const [total, setTotal] = useState(0)
  const listId = useId()

  useDismiss(wrapRef, open, () => setOpen(false))

  useEffect(() => {
    if (autoFocus) boxRef.current?.focus()
    // Read once, on the mount the press caused — Thread's own reasoning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounced, because this fires per keystroke inside an @ word and the
  // alternative is a request for "j", "ja", "jam" on the way to "jamie".
  useEffect(() => {
    if (!open) return
    let live = true
    const t = setTimeout(() => {
      searchMentionable(query.trim())
        .then((page) => {
          if (!live) return
          setPeople(page.people)
          // REC-54. The @ menu shares `mentionable_people` and had the identical
          // silent truncation — six of however many, with nothing saying so.
          // Worse here than in the picker, because somebody mid-sentence
          // concludes the colleague has no account.
          setTotal(page.total)
          setActive(0)
        })
        // Silent. A picker that cannot reach the server should be an @ menu
        // that does not appear, not an error banner over a reply box.
        .catch(() => {
          if (!live) return
          setPeople([])
          setTotal(0)
        })
    }, 150)
    return () => {
      live = false
      clearTimeout(t)
    }
  }, [query, open])

  function sync(next: string, caret: number) {
    const found = activeMentionQuery(next, caret)
    if (found) {
      setQuery(found.query)
      setFrom(found.from)
      setOpen(true)
    } else {
      setOpen(false)
    }
  }

  function choose(p: MentionablePerson) {
    const el = boxRef.current
    const caret = el?.selectionStart ?? value.text.length
    const { draft, caret: at } = insertMention(value, from, caret, p)
    onChange(draft)
    setOpen(false)

    // Deferred, because React has not written the new value into the DOM node
    // yet and setSelectionRange on the old string lands in the middle of the
    // previous sentence.
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(at, at)
    })
  }

  // 25 Sep: a query may now hold spaces (activeMentionQuery says why), so the
  // menu stands down once a query WITH a space matches nobody — that is
  // somebody typing on past a name, not somebody looking for one. Without a
  // space, "No one by that name" still shows: that IS a search that failed.
  const shown = open && !(people.length === 0 && /\s/.test(query.trim()))

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!shown || people.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => (i + 1) % people.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => (i - 1 + people.length) % people.length)
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      choose(people[active])
    } else if (e.key === 'Escape') {
      // Stop it here. Without this the thread's own dismissal sees the press
      // and the person loses the draft they were part-way through.
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
    }
  }

  return (
    <div className="mentionbox" ref={wrapRef}>
      <textarea
        id={id}
        ref={boxRef}
        className="composer__body"
        rows={rows}
        maxLength={maxLength}
        value={value.text}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        role="combobox"
        aria-expanded={shown}
        aria-controls={shown ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={shown && people.length ? `${listId}-${active}` : undefined}
        onKeyDown={onKeyDown}
        onChange={(e) => {
          // reconcile carries the spans across the edit and drops any the edit
          // touched — so backspacing into a name unmakes the mention rather
          // than leaving an id pointing at words that no longer say it.
          onChange(reconcile(value, e.target.value))
          sync(e.target.value, e.target.selectionStart)
        }}
        // A click or an arrow key can move the caret into or out of an @ word
        // without changing a character, so the menu has to follow the caret and
        // not only the text.
        onSelect={(e) => sync(value.text, (e.target as HTMLTextAreaElement).selectionStart)}
        onBlur={() => setOpen(false)}
      />

      {shown && (
        <div className="picker__panel mentionbox__panel">
          {people.length === 0 ? (
            <p className="picker__none">{COMPOSE[lang].noOneByThatName}</p>
          ) : (
            <ul className="picker__list" id={listId} role="listbox">
              {people.map((p, i) => (
                <li
                  key={p.personId}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === active}
                  data-active={i === active}
                  className="picker__opt"
                  // onMouseDown, not onClick: the textarea's blur fires first on
                  // a click and closes the menu before the choice is made.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    choose(p)
                  }}
                  onMouseEnter={() => setActive(i)}
                >
                  <span className="picker__optlabel">{p.displayName}</span>
                </li>
              ))}
            </ul>
          )}
          {/* REC-54, the same note and the same wording as the picker — one
              home, or the two sentences drift the first time either is edited. */}
          <PickerFoot shown={people.length} total={total} hasQuery={Boolean(query.trim())} />
        </div>
      )}
    </div>
  )
}
